/**
 * The reply a chat turn shows when the model API rejected the request.
 *
 * Before this, such a turn showed the empty-turn notice ("a long investigation
 * used up the turn"), which blamed the conversation for what was really a 400
 * from the API, and the server saved nothing, so the turn vanished on reload.
 * The live case (2026-10-01) was a truncated PDF attached through the Files
 * API: "The PDF specified was not valid".
 *
 * Like EMPTY_TURN_NOTICE this is a system notice, deliberately not in the
 * assistant's voice. Known failures get plain-language guidance; anything else
 * carries the API's own message so it can be reported verbatim.
 */

/** Marks a saved assistant row as this notice (see isApiErrorNotice). */
export const API_ERROR_NOTICE_PREFIX = 'This request could not be completed.'

/** Upstream messages are short; cap what we echo so a pathological one can't flood the chat. */
const MAX_ECHOED_MESSAGE_CHARS = 300

interface KnownFailure {
  pattern: RegExp
  guidance: string
}

const KNOWN_FAILURES: KnownFailure[] = [
  {
    pattern: /pdf specified was not valid|pdf.*(could not|cannot) be (read|processed)/i,
    guidance:
      'One of the attached PDFs could not be read. It may be incomplete (for example an ' +
      'interrupted download), corrupted, or password-protected. Remove it, upload a complete ' +
      'copy, and ask again.',
  },
  {
    pattern: /too many pages|page limit|exceeds.*pages/i,
    guidance:
      'An attached PDF has more pages than the AI can read in one document. Split it into ' +
      'smaller files and upload the parts you need.',
  },
  {
    pattern: /prompt is too long|context (window|length)|too many (input )?tokens/i,
    guidance:
      'This conversation and its attachments are too large for one request. Start a new ' +
      'conversation, or remove attachments you no longer need.',
  },
]

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return String(error)
}

/** The notice text for a turn the API rejected. */
export function chatApiErrorNotice(error: unknown): string {
  const raw = messageOf(error).trim()
  const known = KNOWN_FAILURES.find(({ pattern }) => pattern.test(raw))
  if (known) {
    return `${API_ERROR_NOTICE_PREFIX} ${known.guidance}`
  }
  const echoed = raw.length > MAX_ECHOED_MESSAGE_CHARS ? `${raw.slice(0, MAX_ECHOED_MESSAGE_CHARS)}…` : raw
  return echoed
    ? `${API_ERROR_NOTICE_PREFIX} The AI service returned an error: "${echoed}". Try again; if it keeps happening, remove recently attached files.`
    : `${API_ERROR_NOTICE_PREFIX} The AI service returned an error. Try again; if it keeps happening, remove recently attached files.`
}

/** True when saved content is this notice rather than a real reply. */
export function isApiErrorNotice(content: string | null | undefined): boolean {
  return typeof content === 'string' && content.startsWith(API_ERROR_NOTICE_PREFIX)
}
